import { VisitOutcome, VisitPermissions, VisitState } from '@/types/graphql';
import { Objects } from '@/types/objects';
import { isOutcomeReschedulable, isVisitComplete } from '@/types/visit';
import { useConfigValue } from '@/utils/useConfigValue';

type VisitCancelPermission = {
    state: VisitState;
    permissions: Objects.SubType<VisitPermissions, 'cancel'>;
};
type UseCanCancelVisit = <T extends VisitCancelPermission>(visit?: T | null) => boolean;

export const useCanCancelVisit: UseCanCancelVisit = (visit) => {
    const visitEditCancelUserPermission = useConfigValue('visit.edit.cancel');
    return (
        !!visit &&
        visitEditCancelUserPermission &&
        !isVisitComplete(visit.state) &&
        visit.permissions.cancel
    );
};

type VisitReschedulePermission = {
    state: VisitState;
    permissions: Objects.SubType<VisitPermissions, 'reschedule' | 'assign'>;
};
type UseCanRescheduleVisit = <T extends VisitReschedulePermission>(visit?: T | null) => boolean;

export const useCanRescheduleVisit: UseCanRescheduleVisit = (visit) => {
    return (
        !!visit &&
        !isVisitComplete(visit.state) &&
        visit.permissions.reschedule &&
        visit.permissions.assign
    );
};

type VisitReassignPermission = {
    state: VisitState;
    outcome: VisitOutcome;
    permissions: Objects.SubType<VisitPermissions, 'assign' | 'reschedule'>;
};
type UseCanReassignVisit = <T extends VisitReassignPermission>(visit?: T | null) => boolean;

export const useCanReassignVisit: UseCanReassignVisit = (visit) => {
    return (
        !!visit &&
        (!isVisitComplete(visit.state) || isOutcomeReschedulable(visit.outcome)) &&
        visit.permissions.reschedule &&
        visit.permissions.assign
    );
};

// Helper to check if a visit has services for reassignment
type VisitWithServices = {
    clinicianAppointments:
        | {
              services: { id: string }[];
          }[]
        | null;
};
export const visitHasServices = <T extends VisitWithServices>(visit?: T | null): boolean => {
    if (!visit) return false;
    return (
        visit.clinicianAppointments?.some((appointment) => appointment.services.length > 0) ?? false
    );
};

export const REASSIGN_DISABLED_NO_SERVICES_TOOLTIP =
    'Visit must have a service to be reassigned' as const;
